import React from 'react'
import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'
import { MEDIA } from 'src/utils'

const themes = {
  primary: {
    color: 'var(--the-white)',
    backgroundColor: 'var(--cobasi-blue-light-9)'
  },
  secondary: {
    color: 'var(--cobasi-blue-light-9)',
    backgroundColor: 'var(--the-white)',
    border: '1px solid var(--cobasi-blue-light-9)'
  },
  borderless: {
    color: 'var(--cobasi-blue-light-9)',
    backgroundColor: 'transparent',
    border: 'none'
  }
}

export const ButtonStyle = styled.button`
  border-radius: ${props => props.radius ? props.radius : '99px'};
  margin: ${props => props.margin || '1em 0'};
  font-size: 16px;
  box-shadow: ${props => props.borderBox ? '2px 2px var(--light-gray)' : 'none'};
  width: 100%;
  height: 40px;
  font-family: var(--primary-font);
  font-weight: 600;
  transition: all ease .2s;
  border: none;
  ${props => props.invert && 'flex-direction: row-reverse'};
  ${props => themes[props.theme]}
  ${props => props.border && `border: 1px solid ${props.border} `};
  ${props => props.fontColor && `color: ${props.fontColor} `};
  ${props => props.color && `background-color: ${props.color}`};
  ${props => props.height && `height: ${props.height}`};

  ${props => props.textDecoration && css`
    ${Label} {
      text-decoration: ${props.textDecoration}
    }
  `}
  cursor: pointer;
  &:focus{
    outline: none;
  }

  &:disabled {
    ${props => props.disabledColor
      ? `background-color: ${props.disabledColor}; color: var(--gray-9); border: none;`
      : 'background-color: var(--silver); color: var(--gray-9); border: none;'
    };
    cursor: inherit;
  }

  @media ${MEDIA.MOBILE} {
    &.disabled {
      display: none;
    }
  }
`

const Img = styled.img`
  height: 16px;
  margin: auto;
  ${props => props.iconRight ? 'padding-left: 14px;' : 'padding-right: 8px;'}
  vertical-align: middle;
`

export const Label = styled.span`
  vertical-align: middle;
  font-size: ${props => props.fontSize ? props.fontSize : '1.6rem'};
  line-height: 24px;
`

const Button = ({
  label,
  icon,
  type = 'button',
  color,
  fontColor,
  border,
  height,
  textDecoration,
  borderBox,
  onClick,
  dataTest = 'btn',
  fontSize,
  disabled,
  margin,
  radius,
  disabledColor,
  iconRight,
  theme = 'primary',
  className,
  children
}) => {
  const image = icon ? <Img iconRight={iconRight} src={icon} /> : null
  return (
    <ButtonStyle
      type={type}
      color={color}
      fontColor={fontColor}
      border={border}
      height={height}
      textDecoration={textDecoration}
      borderBox={borderBox}
      onClick={onClick}
      data-test={dataTest}
      disabled={disabled}
      margin={margin}
      radius={radius}
      disabledColor={disabledColor}
      iconRight={iconRight}
      theme={theme}
      className={className}
    >
      {!iconRight && image}
      <Label fontSize={fontSize}>
        {label ?? children}
      </Label>
      {iconRight && image}
    </ButtonStyle>
  )
}

export const FormButton = props => <Button fontColor='var(--the-white)' color='var(--cobasi-blue-light-9)' {...props} />

Button.propTypes = {
  label: PropTypes.string,
  icon: PropTypes.node,
  type: PropTypes.string,
  color: PropTypes.string,
  fontColor: PropTypes.string,
  border: PropTypes.string,
  borderBox: PropTypes.bool,
  onClick: PropTypes.func,
  dataTest: PropTypes.string,
  fontSize: PropTypes.string,
  disabled: PropTypes.bool,
  margin: PropTypes.string,
  radius: PropTypes.string,
  disabledColor: PropTypes.string,
  iconRight: PropTypes.bool,
  theme: PropTypes.oneOf(['primary', 'secondary', 'borderless'])
}

export default Button
